import React from "react";
import styled from "@emotion/styled";
import { useTranslations } from "next-intl";
import { useFormikContext, getIn } from "formik";
import { useQuery } from "@tanstack/react-query";
import { Chip } from "@mui/material";
import autoCompleteField from "@components/auto-complete-field";
import { getSector } from "api/sector.request";

type SectorOption = {
  id: number;
  description: string;
  city_id: number;
};

type Props = {
  /** Nombre del campo en Formik, p. ej. "sector" */
  name: string;
  /** id de ciudad seleccionado (o null si no hay) */
  cityId: number | null;
  /** Máximo de chips visibles */
  limitTags?: number;
  /** Deshabilitar cerrar al seleccionar múltiples */
  disableCloseOnSelect?: boolean;
};

export const SectorsField: React.FC<Props> = ({
  name,
  cityId,
  limitTags = 2,
  disableCloseOnSelect = true,
}) => {
  const t = useTranslations("formRealestateFilters");
  const { values, setFieldValue } = useFormikContext<any>();

  // valor actual del campo en formik (arreglo)
  const value: SectorOption[] = Array.isArray(getIn(values, name))
    ? getIn(values, name)
    : [];

  const [search, setSearch] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const prevCityIdRef = React.useRef<number | null>(null);

  const canFetch = React.useMemo(
    () => !!cityId && (search.trim().length === 0 || search.trim().length > 2),
    [cityId, search]
  );

  const { data, isFetching, dataUpdatedAt } = useQuery(
    ["sectors-by-city", cityId, search],
    () =>
      getSector({
        q: search,
        limit: 10,
        cityId: cityId as number,
      }),
    {
      enabled: canFetch,
      keepPreviousData: false,
    }
  );

  const options = React.useMemo(() => {
    if (!cityId || !canFetch) return [];
    if (!data?.data) return [];
    return adapterSectors(data.data);
  }, [cityId, canFetch, data?.data, dataUpdatedAt]);

  // Cuando cambia la ciudad, limpiar campo y opciones si aplica
  React.useEffect(() => {
    const prevCityId = prevCityIdRef.current;

    if (!cityId) {
      if (value.length) setFieldValue(name, []);
      setSearch("");
    } else if (prevCityId && prevCityId !== cityId) {
      if (value.length) setFieldValue(name, []);
      setSearch("");
    }

    prevCityIdRef.current = cityId;
  }, [cityId, value.length, name, setFieldValue]);

  const handleInputChange = React.useCallback(
    (_event: any, newValue: string, reason: string) => {
      if (reason === "clear" || reason === "reset") {
        setSearch("");
        return;
      }
      if (reason === "input") {
        setSearch(newValue ?? "");
      }
    },
    []
  );

  const handleChange = (_event: any, newValue: SectorOption[]) => {
    setFieldValue(name, newValue ?? []);
  };

  const renderTags = React.useCallback(
    (selected: SectorOption[], getTagProps: any) => {
      if (isOpen || search.trim().length > 0) {
        return null;
      }

      const limit = typeof limitTags === "number" ? limitTags : selected.length;
      const visible = selected.slice(0, limit);
      const hiddenCount = selected.length - visible.length;

      return (
        <>
          {visible.map((option, index) => (
            <TagChip
              {...getTagProps({ index })}
              key={option.id}
              label={option.description}
              size="small"
              title={option.description}
            />
          ))}
          {hiddenCount > 0 && (
            <TagChip key="more-tags" label={`+${hiddenCount}`} size="small" disabled />
          )}
        </>
      );
    },
    [isOpen, limitTags, search]
  );

  return (
    <>
      <StyledAutoComplete
        name={name}
        multiple
        limitTags={limitTags}
        options={options}
        value={value}
        onChange={handleChange}
        onInputChange={handleInputChange}
        getOptionLabel={(opt: any) => opt?.description ?? ""}
        placeholder={t("sector")}
        disabled={!cityId}
        loading={isFetching}
        disableCloseOnSelect={disableCloseOnSelect}
        renderTags={renderTags}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
      />
      {!cityId && <SectorHelperText>{t("sectorCityHint")}</SectorHelperText>}
    </>
  );
};

/** Helpers */
const adapterSectors = (rows: any[] = []): SectorOption[] =>
  rows.map((r) => ({
    id: r.id,
    description: r.description,
    city_id: r.city_id,
  }));

const TagChip = styled(Chip)`
  max-width: 100px;
  height: 26px;
  & .MuiChip-label {
    display: block;
    max-width:100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledAutoComplete = styled(autoCompleteField)`
  fieldset {
    border-radius: 50px;
  }
  .MuiAutocomplete-inputRoot {
    min-height: 46px;
    flex-wrap: nowrap;
    align-items: center;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }
  .MuiAutocomplete-tag {
    margin: 0 4px 0 0;
  }
  input::placeholder {
    color: #000;
    opacity: 1;
    font-weight: 600;
    font-size: 14px;
    padding-left: 10px;
  }
`;
const SectorHelperText = styled.span`
  display: block;
  font-size: 10px;
  margin-top: 4px;
`;

export default SectorsField;
